import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { apiError } from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#080c14');
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const usuarioAtual = String(formData.get('usuario') || usuario).trim();
    const senhaAtual = String(formData.get('senha') || senha);

    setBusy(true);
    setErr(null);
    try {
      await login(usuarioAtual, senhaAtual);
      setUsuario(usuarioAtual);
      setSenha(senhaAtual);
      navigate('/');
    } catch (x) {
      setErr(apiError(x, 'Nao foi possivel conectar ao servidor local.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative h-[100dvh] overflow-hidden bg-[#080c14] text-slate-100">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.032)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.032)_1px,transparent_1px)] bg-[size:56px_56px]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-emerald-500/45" />

      <header className="relative flex h-16 items-center justify-between border-b border-slate-900/90 px-5 md:px-10 lg:px-44">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg border border-slate-800 bg-slate-950">
            <span className="text-sm font-bold leading-none text-emerald-300">7</span>
          </div>
          <div className="leading-tight">
            <div className="h-display text-base font-semibold text-slate-100">7Notes</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Gestao fiscal</div>
          </div>
        </div>

        <div className="hidden items-center gap-2 rounded-full border border-slate-800 bg-slate-950/70 px-3 py-1.5 text-xs font-semibold text-slate-400 sm:flex">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          SEFAZ direto
        </div>
      </header>

      <main className="relative flex h-[calc(100dvh-64px)] flex-col px-5 md:px-10 lg:px-44">
        <section className="flex min-h-0 flex-1 items-center justify-center py-3 md:py-4">
          <form onSubmit={onSubmit} className="w-full max-w-[420px] overflow-hidden rounded-xl border border-slate-800 bg-slate-950/85 shadow-[0_20px_70px_-52px_rgba(0,0,0,0.95)]">
            <div className="border-b border-slate-800 bg-slate-950/70 px-4 py-4">
              <h1 className="h-display text-base font-semibold text-slate-100">Acessar sistema</h1>
              <p className="mt-1 text-xs text-slate-400">Informe suas credenciais para continuar.</p>
            </div>

            <div className="space-y-3.5 px-4 py-4">
              <div>
                <label htmlFor="login-usuario" className="field-label">Usuario</label>
                <div className="relative">
                  <input
                    id="login-usuario"
                    type="text"
                    autoFocus
                    name="usuario"
                    autoComplete="username"
                    value={usuario}
                    onChange={(e) => setUsuario(e.target.value)}
                    className="field-input h-10 pr-11 text-sm"
                    placeholder="Seu usuario"
                  />
                  <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md bg-emerald-500 p-1.5 text-slate-950">
                    <KeyRound className="h-3.5 w-3.5" />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="login-senha" className="field-label">Senha</label>
                <div className="relative">
                  <input
                    id="login-senha"
                    type={show ? 'text' : 'password'}
                    name="senha"
                    autoComplete="current-password"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    className="field-input h-10 pr-20 text-sm"
                    placeholder="Sua senha"
                  />
                  <div className="pointer-events-none absolute right-11 top-1/2 -translate-y-1/2 rounded-md bg-emerald-500 p-1.5 text-slate-950">
                    <KeyRound className="h-3.5 w-3.5" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
                    aria-label={show ? 'Esconder senha' : 'Mostrar senha'}
                  >
                    {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              {err && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
                  {err}
                </div>
              )}

              <button type="submit" className="btn-primary min-h-10 w-full" disabled={busy}>
                {busy ? 'Entrando...' : 'Entrar'}
              </button>
            </div>
          </form>
        </section>

        <footer className="hidden shrink-0 grid-cols-3 gap-8 border-t border-slate-900/90 py-4 text-xs text-slate-600 md:grid">
          <div>
            <div className="mb-2 flex items-center gap-2 text-slate-300">
              <span className="text-xs font-bold text-emerald-300">7</span>
              <span className="h-display text-base font-semibold">7Notes</span>
            </div>
            <p className="max-w-sm leading-5">
              Sistema de gestao e emissao de documentos fiscais eletronicos.
            </p>
          </div>
          <div>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Modulos</div>
            <ul className="space-y-1 leading-5">
              <li>NF-e / NF-e Devolucao</li>
              <li>CT-e / CT-e OS</li>
              <li>MDF-e</li>
            </ul>
          </div>
          <div>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Integracao</div>
            <ul className="space-y-1 leading-5">
              <li>SEFAZ direto</li>
              <li>Certificado A1</li>
              <li>XML, DANFE e eventos</li>
            </ul>
          </div>
        </footer>
      </main>
    </div>
  );
}
